import {
  ACCENT_FAMILIES,
  FIDELITY_STAGES,
  FIDELITY_STATUSES,
  POLICY_MODES,
  type AccentFamily,
  type FidelityStage,
  type FidelityStatus,
  type PolicyMode,
} from "../echoflicks/voiceFidelity";

/**
 * `summary.accent_fidelity` from `GET /sound/voices` (VAF-12): integer counts
 * of recent Echoes with an accent requirement and the acoustic receipts the
 * pipeline reported for them. Internal/no-delivery canaries are excluded by
 * the dashboard API. Counts are evaluator reports, never human listening and
 * never delivery; the final verdict is the encoded-audio receipt only.
 */
export type AccentFidelityUsage = {
  status: "live" | "unavailable";
  windowDays: number | null;
  truncated: boolean;
  sessionsWithRequirement: number;
  byRequestedAccent: Record<AccentFamily, number>;
  byPolicyMode: Record<PolicyMode | "unknown", number>;
  receiptsByStage: Record<FidelityStage, Record<FidelityStatus, number>>;
  finalVerdicts: Record<FidelityStatus, number>;
  unverifiedCount: number;
  evidenceWithoutRequirementCount: number;
  excludedInternalCount: number;
};

/** Defensive parse; null when the API predates the aggregate. Never throws. */
export function parseAccentFidelityUsage(
  value: unknown,
): AccentFidelityUsage | null {
  const raw = recordValue(value);
  if (!raw || (raw.status !== "live" && raw.status !== "unavailable")) {
    return null;
  }
  const stages = recordValue(raw.receipts_by_stage);
  return {
    status: raw.status,
    windowDays: count(raw.window_days) || null,
    truncated: raw.truncated === true,
    sessionsWithRequirement: count(raw.sessions_with_requirement),
    byRequestedAccent: counts(raw.by_requested_accent, ACCENT_FAMILIES),
    byPolicyMode: counts(raw.by_policy_mode, [...POLICY_MODES, "unknown"]),
    receiptsByStage: Object.fromEntries(
      FIDELITY_STAGES.map((stage) => [
        stage,
        counts(stages?.[stage], FIDELITY_STATUSES),
      ]),
    ) as Record<FidelityStage, Record<FidelityStatus, number>>,
    finalVerdicts: counts(raw.final_verdicts, FIDELITY_STATUSES),
    unverifiedCount: count(raw.unverified_count),
    evidenceWithoutRequirementCount: count(
      raw.evidence_without_requirement_count,
    ),
    excludedInternalCount: count(raw.excluded_internal_count),
  };
}

function counts<K extends string>(
  value: unknown,
  keys: readonly K[],
): Record<K, number> {
  const raw = recordValue(value);
  return Object.fromEntries(
    keys.map((key) => [key, count(raw?.[key])]),
  ) as Record<K, number>;
}

function count(value: unknown): number {
  return typeof value === "number" && Number.isInteger(value) && value >= 0
    ? value
    : 0;
}

function recordValue(value: unknown): Record<string, unknown> | null {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}
